import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Pagination from "@/components/base/Pagination";
import { formatDateTime } from "@/lib/format";
import {
  fetchEntryClicks,
  fetchEntryUsageCosts,
  fetchFunnel,
  fetchGenerationQuality,
  fetchOverview,
  fetchRecentErrors,
  fetchTabClicks,
  fetchTrends,
  resolveDateRange,
  type DateRangePreset,
  type EntryClickItem,
  type EntryUsageCostItem,
  type EntryUsageCostsResult,
  type ErrorRankItem,
  type FunnelStep,
  type KpiOverview,
  type QualityTrendPoint,
  type RecentErrorItem,
  type TabClickItem,
  type TrendPoint,
} from "./api";

function formatNum(value: number): string {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value.toLocaleString("zh-CN");
}

function formatRate(value: number): string {
  return `${Number(value || 0).toFixed(1)}%`;
}

function formatMoney(value: number, currency: "CNY" | "USD"): string {
  const symbol = currency === "CNY" ? "¥" : "$";
  return `${symbol}${Number(value || 0).toLocaleString("zh-CN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function currencyAmount(overview: KpiOverview | null, currency: "CNY" | "USD"): number {
  return overview?.newRevenueByCurrency.find((item) => item.currency.toUpperCase() === currency)?.amount ?? 0;
}

function formatCompare(
  current: number,
  previous: number,
  isRate = false,
  lowerIsBetter = false,
): { text: string; tone: string } {
  if (!previous && !current) return { text: "较昨日持平", tone: "text-text-muted" };
  if (!previous) return { text: "较昨日新增", tone: current > 0 ? "text-emerald-600" : "text-text-muted" };

  const diff = current - previous;
  if (!diff) return { text: "较昨日持平", tone: "text-text-muted" };
  const isGood = lowerIsBetter ? diff < 0 : diff > 0;

  if (isRate) {
    return {
      text: `较昨日${diff > 0 ? "+" : ""}${diff.toFixed(1)}pt`,
      tone: isGood ? "text-emerald-600" : "text-red-600",
    };
  }

  const pct = (diff / previous) * 100;
  return {
    text: `较昨日${diff > 0 ? "+" : ""}${pct.toFixed(1)}%`,
    tone: isGood ? "text-emerald-600" : "text-red-600",
  };
}

function periodLabel(period: DateRangePreset): string {
  if (period === "week") return "本周";
  if (period === "month") return "本月";
  if (period === "quarter") return "本季度";
  return "今日";
}

function maxOf<T>(items: T[], getter: (item: T) => number): number {
  return Math.max(...items.map(getter), 1);
}

function chartDateLabel(item: Record<string, number | string>): string {
  return String(item.date ?? "未知日期");
}

function shortDateLabel(item: Record<string, number | string>): string {
  const label = chartDateLabel(item);
  return label.length > 5 ? label.slice(5) : label;
}

function ChartTooltip({
  xPercent,
  title,
  rows,
}: {
  xPercent: number;
  title: string;
  rows: Array<{ label: string; value: number; color: string }>;
}) {
  return (
    <div
      className="pointer-events-none absolute top-3 z-10 min-w-[180px] -translate-x-1/2 rounded-lg border border-surface-border bg-white px-3 py-2 text-xs shadow-lg"
      style={{ left: `${Math.min(Math.max(xPercent, 16), 84)}%` }}
    >
      <p className="mb-2 font-medium text-text-primary">{title}</p>
      <div className="space-y-1.5">
        {rows.map((row) => (
          <div key={row.label} className="flex items-center justify-between gap-5">
            <span className="inline-flex items-center gap-1.5 text-text-secondary">
              <span className="h-2 w-2 rounded-sm" style={{ backgroundColor: row.color }} />
              {row.label}
            </span>
            <span className="font-mono text-text-primary">{formatNum(row.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MetricCard({
  label,
  value,
  icon,
  tone,
  compare,
}: {
  label: string;
  value: string;
  icon: string;
  tone: string;
  compare?: { text: string; tone: string };
}) {
  return (
    <div className="rounded-xl border border-surface-border bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-xs text-text-muted">{label}</span>
        <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${tone}`}>
          <i className={`${icon} text-base`} />
        </span>
      </div>
      <p className="font-mono text-2xl font-semibold text-text-primary">{value}</p>
      {compare && <p className={`mt-2 text-xs ${compare.tone}`}>{compare.text}</p>}
    </div>
  );
}

function LineTrend({
  data,
  keys,
}: {
  data: Array<Record<string, number | string>>;
  keys: Array<{ key: string; label: string; color: string }>;
}) {
  const width = 820;
  const height = 260;
  const pad = { top: 16, right: 16, bottom: 30, left: 54 };
  const chartWidth = width - pad.left - pad.right;
  const chartHeight = height - pad.top - pad.bottom;
  const max = Math.max(...data.flatMap((item) => keys.map((line) => Number(item[line.key] ?? 0))), 1);
  const xStep = data.length > 1 ? chartWidth / (data.length - 1) : chartWidth;
  const x = (index: number) => pad.left + index * xStep;
  const y = (value: number) => pad.top + chartHeight - (value / max) * chartHeight;
  const yTicks = Array.from({ length: 5 }, (_, index) => Math.round((max / 4) * index));
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const activeItem = activeIndex === null ? null : data[activeIndex];

  if (data.length === 0) {
    return <div className="py-10 text-center text-sm text-text-muted">暂无数据</div>;
  }

  return (
    <div className="space-y-3">
      <div className="relative" onMouseLeave={() => setActiveIndex(null)}>
        {activeItem && activeIndex !== null && (
          <ChartTooltip
            xPercent={(x(activeIndex) / width) * 100}
            title={chartDateLabel(activeItem)}
            rows={keys.map((line) => ({
              label: line.label,
              value: Number(activeItem[line.key] ?? 0),
              color: line.color,
            }))}
          />
        )}
        <svg viewBox={`0 0 ${width} ${height}`} className="h-auto w-full" preserveAspectRatio="xMidYMid meet">
        {yTicks.map((tick) => {
          const yPos = y(tick);
          return (
            <g key={tick}>
              <line x1={pad.left} x2={width - pad.right} y1={yPos} y2={yPos} stroke="#e2e8f0" strokeWidth="0.5" />
              <text x={pad.left - 8} y={yPos + 4} textAnchor="end" fill="#94a3b8" fontSize="10">
                {formatNum(tick)}
              </text>
            </g>
          );
        })}
        {activeIndex !== null && (
          <line
            x1={x(activeIndex)}
            x2={x(activeIndex)}
            y1={pad.top}
            y2={pad.top + chartHeight}
            stroke="#64748b"
            strokeDasharray="4 4"
            strokeWidth="1"
          />
        )}
        {keys.map((line) => {
          const points = data
            .map((item, index) => `${x(index)},${y(Number(item[line.key] ?? 0))}`)
            .join(" ");
          return (
            <g key={line.key}>
              <polyline
                points={points}
                fill="none"
                stroke={line.color}
                strokeWidth="1.8"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
              {data.map((item, index) => (
                <circle
                  key={`${line.key}-${index}`}
                  cx={x(index)}
                  cy={y(Number(item[line.key] ?? 0))}
                  r={activeIndex === index ? "4.5" : "3"}
                  fill={line.color}
                  stroke={activeIndex === index ? "#ffffff" : "none"}
                  strokeWidth={activeIndex === index ? "2" : "0"}
                />
              ))}
            </g>
          );
        })}
        {data.map((_, index) => {
          const bandWidth = data.length > 1 ? xStep : chartWidth;
          return (
            <rect
              key={`hit-${index}`}
              x={x(index) - bandWidth / 2}
              y={pad.top}
              width={bandWidth}
              height={chartHeight}
              fill="transparent"
              onMouseEnter={() => setActiveIndex(index)}
              onFocus={() => setActiveIndex(index)}
              tabIndex={0}
              aria-label={`${chartDateLabel(data[index])} 数据`}
            />
          );
        })}
        {data.map((item, index) => {
          if (data.length > 14 && index % Math.ceil(data.length / 7) !== 0 && index !== data.length - 1) return null;
          return (
            <text key={String(item.date)} x={x(index)} y={height - 10} textAnchor="middle" fill="#94a3b8" fontSize="10">
              {shortDateLabel(item)}
            </text>
          );
        })}
        </svg>
      </div>
      <div className="flex flex-wrap justify-center gap-4 text-xs text-text-secondary">
        {keys.map((line) => (
          <span key={line.key} className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-sm" style={{ backgroundColor: line.color }} />
            {line.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function HorizontalBars({
  data,
  label,
  value,
  color,
  valueText,
}: {
  data: unknown[];
  label: (item: unknown) => string;
  value: (item: unknown) => number;
  color: string;
  valueText?: (item: unknown) => string;
}) {
  const max = maxOf(data, value);
  if (data.length === 0) {
    return <div className="py-10 text-center text-sm text-text-muted">暂无数据</div>;
  }
  return (
    <div className="space-y-3">
      {data.map((item) => {
        const val = value(item);
        return (
          <div key={label(item)} className="grid grid-cols-[120px_1fr_96px] items-center gap-3 text-xs">
            <span className="truncate text-right text-text-secondary">{label(item)}</span>
            <div className="h-5 overflow-hidden rounded-full bg-surface">
              <div
                className="h-full rounded-full"
                style={{ width: `${Math.max((val / max) * 100, 1)}%`, backgroundColor: color }}
              />
            </div>
            <span className="font-mono text-text-primary">{valueText ? valueText(item) : formatNum(val)}</span>
          </div>
        );
      })}
    </div>
  );
}

const ENTRY_COLORS = ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#14b8a6", "#8b5cf6", "#ec4899"];

function DailyEntryStackedBars({
  data,
  metric,
  valueText,
}: {
  data: EntryUsageCostItem[];
  metric: "totalTokens" | "estimatedImageCostUsd";
  valueText: (value: number) => string;
}) {
  const entryNames = Array.from(new Map(data.map((item) => [item.entryId, item.entryName])).entries());
  const grouped = Array.from(
    data.reduce((dates, item) => {
      const date = item.date || "未知日期";
      const current = dates.get(date) ?? [];
      current.push(item);
      dates.set(date, current);
      return dates;
    }, new Map<string, EntryUsageCostItem[]>()),
  ).sort(([left], [right]) => left.localeCompare(right));
  const maxTotal = Math.max(
    ...grouped.map(([, items]) => items.reduce((sum, item) => sum + item[metric], 0)),
    1,
  );

  if (!grouped.length) return <div className="py-10 text-center text-sm text-text-muted">暂无数据</div>;

  return (
    <div className="space-y-4">
      {grouped.map(([date, items]) => {
        const total = items.reduce((sum, item) => sum + item[metric], 0);
        return (
          <div key={date} className="grid grid-cols-[72px_1fr_88px] items-center gap-3 text-xs">
            <span className="font-mono text-text-muted">{date.slice(5)}</span>
            <div className="h-6 overflow-hidden rounded-md bg-surface">
              <div className="flex h-full" style={{ width: `${Math.max((total / maxTotal) * 100, total ? 2 : 0)}%` }}>
                {items.map((item) => {
                  const colorIndex = Math.max(entryNames.findIndex(([id]) => id === item.entryId), 0);
                  return (
                    <div
                      key={`${date}-${item.entryId}`}
                      title={`${item.entryName}: ${valueText(item[metric])}`}
                      style={{
                        width: `${total ? (item[metric] / total) * 100 : 0}%`,
                        backgroundColor: ENTRY_COLORS[colorIndex % ENTRY_COLORS.length],
                      }}
                    />
                  );
                })}
              </div>
            </div>
            <span className="text-right font-mono text-text-primary">{valueText(total)}</span>
          </div>
        );
      })}
      <div className="flex flex-wrap gap-3 text-xs text-text-secondary">
        {entryNames.map(([entryId, entryName], index) => (
          <span key={entryId} className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-sm" style={{ backgroundColor: ENTRY_COLORS[index % ENTRY_COLORS.length] }} />
            {entryName}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function AiReportsPage() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState<KpiOverview | null>(null);
  const [yesterdayOverview, setYesterdayOverview] = useState<KpiOverview | null>(null);
  const [periodOverview, setPeriodOverview] = useState<KpiOverview | null>(null);
  const [periodPreset, setPeriodPreset] = useState<DateRangePreset>("week");
  const [trends, setTrends] = useState<TrendPoint[]>([]);
  const [funnel, setFunnel] = useState<FunnelStep[]>([]);
  const [entryClicks, setEntryClicks] = useState<EntryClickItem[]>([]);
  const [tabClicks, setTabClicks] = useState<TabClickItem[]>([]);
  const [qualityTrends, setQualityTrends] = useState<QualityTrendPoint[]>([]);
  const [errorTop, setErrorTop] = useState<ErrorRankItem[]>([]);
  const [entryUsageCosts, setEntryUsageCosts] = useState<EntryUsageCostsResult>({
    summary: {
      deliveredImages: 0,
      providerCalls: 0,
      modelCalls: 0,
      inputTokens: 0,
      outputTokens: 0,
      cachedInputTokens: 0,
      reasoningTokens: 0,
      totalTokens: 0,
      estimatedImageCostUsd: 0,
      threeImageEstimateUsd: null,
      threeImageEstimateBasis: "unavailable",
      tokenCoverageRate: 0,
    },
    dailyEntries: [],
    entrySummary: [],
  });
  const [recentErrors, setRecentErrors] = useState<RecentErrorItem[]>([]);
  const [recentErrorsTotal, setRecentErrorsTotal] = useState(0);
  const [recentErrorsPage, setRecentErrorsPage] = useState(1);
  const [recentErrorsPageSize, setRecentErrorsPageSize] = useState(10);
  const [selectedReasonCode, setSelectedReasonCode] = useState("");
  const [selectedEntry, setSelectedEntry] = useState("");
  const [selectedTab, setSelectedTab] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const totalPages = Math.max(Math.ceil(recentErrorsTotal / recentErrorsPageSize), 1);

  const loadReports = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const todayRange = resolveDateRange("today");
      const yesterdayRange = resolveDateRange("yesterday");
      const periodRange = resolveDateRange(periodPreset);
      const sevenDayRange = resolveDateRange("7d");
      const [
        nextOverview,
        nextYesterdayOverview,
        nextPeriodOverview,
        nextTrends,
        nextFunnel,
        nextEntries,
        nextTabs,
        nextQuality,
        nextCosts,
      ] =
        await Promise.all([
          fetchOverview(todayRange),
          fetchOverview(yesterdayRange),
          fetchOverview(periodRange),
          fetchTrends(periodRange),
          fetchFunnel(todayRange),
          fetchEntryClicks(todayRange),
          fetchTabClicks(todayRange),
          fetchGenerationQuality(sevenDayRange),
          fetchEntryUsageCosts(periodRange),
        ]);

      setOverview(nextOverview);
      setYesterdayOverview(nextYesterdayOverview);
      setPeriodOverview(nextPeriodOverview);
      setTrends(nextTrends);
      setFunnel(nextFunnel);
      setEntryClicks(nextEntries);
      setTabClicks(nextTabs);
      setQualityTrends(nextQuality.trend);
      setErrorTop(nextQuality.errorTop);
      setEntryUsageCosts(nextCosts);

      const nextErrors = await fetchRecentErrors(sevenDayRange, 1, recentErrorsPageSize, selectedReasonCode);
      setRecentErrors(nextErrors.items);
      setRecentErrorsTotal(nextErrors.total);
      setRecentErrorsPage(1);
    } catch (err) {
      console.error("Fetch AI reports error:", err);
      setError("报表数据加载失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  }, [periodPreset, recentErrorsPageSize, selectedReasonCode]);

  const loadRecentErrors = useCallback(
    async (page: number, pageSize: number, reasonCode = selectedReasonCode) => {
      const sevenDayRange = resolveDateRange("7d");
      const nextErrors = await fetchRecentErrors(sevenDayRange, page, pageSize, reasonCode);
      setRecentErrors(nextErrors.items);
      setRecentErrorsTotal(nextErrors.total);
      setRecentErrorsPage(page);
      setRecentErrorsPageSize(pageSize);
    },
    [selectedReasonCode],
  );

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const metricCards = overview
    ? [
        {
          label: "登录用户",
          value: formatNum(overview.loginUsers),
          icon: "ri-user-line",
          tone: "bg-brand/10 text-brand",
          compare: yesterdayOverview ? formatCompare(overview.loginUsers, yesterdayOverview.loginUsers) : undefined,
        },
        {
          label: "新增用户",
          value: formatNum(overview.newUsers),
          icon: "ri-user-add-line",
          tone: "bg-emerald-500/10 text-emerald-500",
          compare: yesterdayOverview ? formatCompare(overview.newUsers, yesterdayOverview.newUsers) : undefined,
        },
        {
          label: "AI 生图成功率",
          value: formatRate(overview.generationSuccessRate),
          icon: "ri-percent-line",
          tone: "bg-brand/10 text-brand",
          compare: yesterdayOverview
            ? formatCompare(overview.generationSuccessRate, yesterdayOverview.generationSuccessRate, true)
            : undefined,
        },
        {
          label: "AI 生图失败",
          value: formatNum(overview.generationFailed),
          icon: "ri-close-circle-line",
          tone: "bg-red-500/10 text-red-500",
          compare: yesterdayOverview ? formatCompare(overview.generationFailed, yesterdayOverview.generationFailed, false, true) : undefined,
        },
        {
          label: "AI 生图总数",
          value: formatNum(overview.generationSubmits),
          icon: "ri-image-ai-line",
          tone: "bg-rose-500/10 text-rose-500",
          compare: yesterdayOverview ? formatCompare(overview.generationSubmits, yesterdayOverview.generationSubmits) : undefined,
        },
        {
          label: "新增会员数",
          value: formatNum(overview.newMembers),
          icon: "ri-vip-crown-line",
          tone: "bg-amber-500/10 text-amber-500",
          compare: yesterdayOverview ? formatCompare(overview.newMembers, yesterdayOverview.newMembers) : undefined,
        },
        {
          label: "新增金额",
          value: overview.newRevenue.toFixed(2),
          icon: "ri-money-dollar-circle-line",
          tone: "bg-purple-500/10 text-purple-500",
          compare: yesterdayOverview ? formatCompare(overview.newRevenue, yesterdayOverview.newRevenue) : undefined,
        },
        {
          label: "首页访问 UV",
          value: formatNum(overview.homeUv),
          icon: "ri-eye-line",
          tone: "bg-teal-500/10 text-teal-500",
          compare: yesterdayOverview ? formatCompare(overview.homeUv, yesterdayOverview.homeUv) : undefined,
        },
      ]
    : [];
  const periodRows = periodOverview
    ? [
        { label: "累计营收 CNY", value: formatMoney(currencyAmount(periodOverview, "CNY"), "CNY") },
        { label: "累计营收 USD", value: formatMoney(currencyAmount(periodOverview, "USD"), "USD") },
        { label: "新增用户", value: formatNum(periodOverview.newUsers) },
        { label: "登录用户", value: formatNum(periodOverview.loginUsers) },
        { label: "新增 VIP", value: formatNum(periodOverview.newMembers) },
        { label: "总生图请求", value: formatNum(periodOverview.generationSubmits) },
        { label: "成功率", value: formatRate(periodOverview.generationSuccessRate) },
        { label: "AI 总请求次数", value: formatNum(periodOverview.generationSubmits) },
        { label: "积分消耗", value: formatNum(periodOverview.pointsConsumed) },
        { label: "生图失败 Case", value: formatNum(periodOverview.generationFailed) },
      ]
    : [];

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-center text-text-muted">
        <div>
          <i className="ri-loader-4-line mb-2 block animate-spin text-3xl text-brand" />
          加载报表数据...
        </div>
      </div>
    );
  }

  if (error || !overview) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-center">
        <div>
          <i className="ri-error-warning-line mb-3 block text-4xl text-red-500" />
          <p className="mb-4 text-sm text-text-primary">{error || "暂无报表数据"}</p>
          <button
            onClick={loadReports}
            className="h-9 rounded-lg bg-brand px-4 text-sm font-medium text-white hover:bg-brand-dark"
          >
            重试
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-12">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h2 className="text-xl font-semibold text-text-primary">数据报表</h2>
          <p className="mt-1 text-sm text-text-secondary">
            今日实时数据、整体运营周期统计、入口点击漏斗和失败 Case 分析
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={loadReports}
            className="h-9 rounded-lg border border-surface-border bg-white px-3 text-xs font-medium text-text-secondary hover:border-brand/40 hover:text-brand"
          >
            <i className="ri-refresh-line mr-1" />
            刷新
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {metricCards.map((card) => (
          <MetricCard
            key={card.label}
            label={card.label}
            value={card.value}
            icon={card.icon}
            tone={card.tone}
            compare={card.compare}
          />
        ))}
      </div>

      <section className="rounded-xl border border-surface-border bg-white">
        <div className="flex flex-col justify-between gap-3 border-b border-surface-border px-5 py-4 sm:flex-row sm:items-center">
          <div>
            <h3 className="text-base font-medium text-text-primary">整体运营</h3>
            <p className="mt-1 text-xs text-text-muted">按周、月、季度汇总关键运营指标</p>
          </div>
          <div className="inline-flex w-fit overflow-hidden rounded-lg border border-surface-border bg-surface/50 p-0.5 text-xs">
            {(["week", "month", "quarter"] as DateRangePreset[]).map((period) => (
              <button
                key={period}
                onClick={() => setPeriodPreset(period)}
                className={`h-8 px-3 font-medium transition-colors ${
                  periodPreset === period ? "rounded-md bg-white text-brand shadow-sm" : "text-text-secondary hover:text-text-primary"
                }`}
              >
                {periodLabel(period)}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-px bg-surface-border md:grid-cols-5">
          {periodRows.map((row) => (
            <div key={row.label} className="bg-white px-4 py-3">
              <p className="text-xs text-text-muted">{row.label}</p>
              <p className="mt-1 font-mono text-lg font-semibold text-text-primary">{row.value}</p>
            </div>
          ))}
        </div>
      </section>

      {(selectedEntry || selectedTab || selectedReasonCode) && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-brand/20 bg-brand/5 px-4 py-3 text-xs text-text-secondary">
          <span className="font-medium text-text-primary">当前高亮</span>
          {selectedEntry && (
            <button
              onClick={() => setSelectedEntry("")}
              className="rounded-full bg-white px-3 py-1 text-brand shadow-sm"
            >
              入口：{entryClicks.find((item) => item.entryId === selectedEntry)?.entryName || selectedEntry}
              <i className="ri-close-line ml-1" />
            </button>
          )}
          {selectedTab && (
            <button
              onClick={() => setSelectedTab("")}
              className="rounded-full bg-white px-3 py-1 text-brand shadow-sm"
            >
              Tab：{tabClicks.find((item) => item.tab === selectedTab)?.tabName || selectedTab}
              <i className="ri-close-line ml-1" />
            </button>
          )}
          {selectedReasonCode && (
            <button
              onClick={async () => {
                setSelectedReasonCode("");
                await loadRecentErrors(1, recentErrorsPageSize, "");
              }}
              className="rounded-full bg-white px-3 py-1 text-brand shadow-sm"
            >
              reason_code：{selectedReasonCode}
              <i className="ri-close-line ml-1" />
            </button>
          )}
        </div>
      )}

      <section className="rounded-xl border border-surface-border bg-white">
        <div className="border-b border-surface-border px-5 py-4">
          <h3 className="text-base font-medium text-text-primary">用户增长与首页访问趋势</h3>
          <p className="mt-1 text-xs text-text-muted">{periodLabel(periodPreset)}趋势，按北京时间统计</p>
        </div>
        <div className="p-5">
          <LineTrend
            data={trends as unknown as Array<Record<string, number | string>>}
            keys={[
              { key: "loginUsers", label: "登录用户", color: "#10b981" },
              { key: "newUsers", label: "新增用户", color: "#14b8a6" },
              { key: "homeUv", label: "首页访问 UV", color: "#f59e0b" },
            ]}
          />
          <div className="mt-5 overflow-x-auto">
            <table className="w-full min-w-[860px] text-xs">
              <thead className="bg-surface/50 text-text-secondary">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">日期</th>
                  <th className="px-3 py-2 text-right font-medium">新增用户</th>
                  <th className="px-3 py-2 text-right font-medium">登录用户</th>
                  <th className="px-3 py-2 text-right font-medium">首页 UV</th>
                  <th className="px-3 py-2 text-right font-medium">生图请求</th>
                  <th className="px-3 py-2 text-right font-medium">成功</th>
                  <th className="px-3 py-2 text-right font-medium">失败</th>
                  <th className="px-3 py-2 text-right font-medium">拦截</th>
                  <th className="px-3 py-2 text-right font-medium">营收 CNY</th>
                  <th className="px-3 py-2 text-right font-medium">营收 USD</th>
                </tr>
              </thead>
              <tbody>
                {trends.map((item) => (
                  <tr key={item.date} className="border-b border-surface-border">
                    <td className="px-3 py-2 font-mono text-text-primary">{item.date}</td>
                    <td className="px-3 py-2 text-right font-mono">{formatNum(item.newUsers)}</td>
                    <td className="px-3 py-2 text-right font-mono">{formatNum(item.loginUsers)}</td>
                    <td className="px-3 py-2 text-right font-mono">{formatNum(item.homeUv)}</td>
                    <td className="px-3 py-2 text-right font-mono">{formatNum(item.generationSubmits)}</td>
                    <td className="px-3 py-2 text-right font-mono">{formatNum(item.generationSuccess)}</td>
                    <td className="px-3 py-2 text-right font-mono">{formatNum(item.generationFailed)}</td>
                    <td className="px-3 py-2 text-right font-mono">{formatNum(item.generationBlocked)}</td>
                    <td className="px-3 py-2 text-right font-mono">{formatMoney(item.revenueCny, "CNY")}</td>
                    <td className="px-3 py-2 text-right font-mono">{formatMoney(item.revenueUsd, "USD")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="rounded-xl border border-surface-border bg-white">
        <div className="border-b border-surface-border px-5 py-4">
          <h3 className="text-base font-medium text-text-primary">用户转化漏斗</h3>
        </div>
        <div className="p-5">
          <div className="mx-auto max-w-4xl space-y-3">
            {funnel.map((step) => {
              const maxUv = Math.max(funnel[0]?.uv ?? 0, 1);
              return (
                <div key={step.eventName} className="grid grid-cols-[120px_1fr_150px] items-center gap-4 text-xs">
                  <span className="text-right text-text-secondary">{step.label}</span>
                  <div className="h-8 overflow-hidden rounded-lg bg-surface">
                    <div
                      className="flex h-full items-center rounded-lg bg-brand/80 px-3 text-white"
                      style={{ width: `${Math.max((step.uv / maxUv) * 100, 2)}%` }}
                    >
                      <span className="font-mono">{formatNum(step.uv)}</span>
                    </div>
                  </div>
                  <span className="text-text-muted">
                    上层 {formatRate(step.stepRate)} / 总 {formatRate(step.overallRate)}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="mt-5 overflow-x-auto">
            <table className="w-full min-w-[720px] text-xs">
              <thead className="bg-surface/50 text-text-secondary">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">步骤</th>
                  <th className="px-3 py-2 text-right font-medium">UV</th>
                  <th className="px-3 py-2 text-right font-medium">上一步转化</th>
                  <th className="px-3 py-2 text-right font-medium">总体转化</th>
                  <th className="px-3 py-2 text-right font-medium">流失</th>
                </tr>
              </thead>
              <tbody>
                {funnel.map((step) => (
                  <tr key={`${step.eventName}-row`} className="border-b border-surface-border">
                    <td className="px-3 py-2 text-text-primary">{step.label}</td>
                    <td className="px-3 py-2 text-right font-mono">{formatNum(step.uv)}</td>
                    <td className="px-3 py-2 text-right font-mono">{formatRate(step.stepRate)}</td>
                    <td className="px-3 py-2 text-right font-mono">{formatRate(step.overallRate)}</td>
                    <td className="px-3 py-2 text-right font-mono">{formatNum(step.dropOff)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        <section className="rounded-xl border border-surface-border bg-white">
          <div className="border-b border-surface-border px-5 py-4">
            <h3 className="text-base font-medium text-text-primary">首页入口点击分析</h3>
          </div>
          <div className="p-5">
            <HorizontalBars
              data={entryClicks}
              label={(item) => (item as EntryClickItem).entryName}
              value={(item) => (item as EntryClickItem).clickUv}
              color="#10b981"
            />
            <div className="mt-5 overflow-x-auto">
              <table className="w-full min-w-[760px] text-xs">
                <thead className="bg-surface/50 text-text-secondary">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">入口</th>
                    <th className="px-3 py-2 text-right font-medium">曝光 UV</th>
                    <th className="px-3 py-2 text-right font-medium">点击 UV</th>
                    <th className="px-3 py-2 text-right font-medium">点击 PV</th>
                    <th className="px-3 py-2 text-right font-medium">CTR</th>
                    <th className="px-3 py-2 text-right font-medium">提交数</th>
                  </tr>
                </thead>
                <tbody>
                  {entryClicks.map((item) => (
                    <tr
                      key={item.entryId}
                      onClick={() => setSelectedEntry((current) => (current === item.entryId ? "" : item.entryId))}
                      className={`cursor-pointer border-b border-surface-border transition-colors ${
                        selectedEntry === item.entryId ? "bg-brand/5" : "hover:bg-surface/30"
                      }`}
                    >
                      <td className="px-3 py-2 text-text-primary">{item.entryName}</td>
                      <td className="px-3 py-2 text-right font-mono">{formatNum(item.exposureUv)}</td>
                      <td className="px-3 py-2 text-right font-mono">{formatNum(item.clickUv)}</td>
                      <td className="px-3 py-2 text-right font-mono">{formatNum(item.clickPv)}</td>
                      <td className="px-3 py-2 text-right font-mono">{formatRate(item.ctr)}</td>
                      <td className="px-3 py-2 text-right font-mono">{formatNum(item.generationSubmits)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="rounded-xl border border-surface-border bg-white">
          <div className="border-b border-surface-border px-5 py-4">
            <h3 className="text-base font-medium text-text-primary">生图页 Tab 点击分析</h3>
          </div>
          <div className="p-5">
            <HorizontalBars
              data={tabClicks}
              label={(item) => (item as TabClickItem).tabName}
              value={(item) => (item as TabClickItem).submits}
              color="#14b8a6"
            />
            <div className="mt-5 overflow-x-auto">
              <table className="w-full min-w-[820px] text-xs">
                <thead className="bg-surface/50 text-text-secondary">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Tab</th>
                    <th className="px-3 py-2 text-right font-medium">访问 UV</th>
                    <th className="px-3 py-2 text-right font-medium">点击 UV</th>
                    <th className="px-3 py-2 text-right font-medium">提交</th>
                    <th className="px-3 py-2 text-right font-medium">成功</th>
                    <th className="px-3 py-2 text-right font-medium">失败</th>
                    <th className="px-3 py-2 text-right font-medium">拦截</th>
                  </tr>
                </thead>
                <tbody>
                  {tabClicks.map((item) => (
                    <tr
                      key={item.tab}
                      onClick={() => setSelectedTab((current) => (current === item.tab ? "" : item.tab))}
                      className={`cursor-pointer border-b border-surface-border transition-colors ${
                        selectedTab === item.tab ? "bg-brand/5" : "hover:bg-surface/30"
                      }`}
                    >
                      <td className="px-3 py-2 text-text-primary">{item.tabName}</td>
                      <td className="px-3 py-2 text-right font-mono">{formatNum(item.pageUv)}</td>
                      <td className="px-3 py-2 text-right font-mono">{formatNum(item.clickUv)}</td>
                      <td className="px-3 py-2 text-right font-mono">{formatNum(item.submits)}</td>
                      <td className="px-3 py-2 text-right font-mono">{formatNum(item.successes)}</td>
                      <td className="px-3 py-2 text-right font-mono">{formatNum(item.failures)}</td>
                      <td className="px-3 py-2 text-right font-mono">{formatNum(item.blocked)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        <section className="rounded-xl border border-surface-border bg-white">
          <div className="border-b border-surface-border px-5 py-4">
            <h3 className="text-base font-medium text-text-primary">AI 生图质量趋势</h3>
            <p className="mt-1 text-xs text-text-muted">固定展示最近 7 天</p>
          </div>
          <div className="p-5">
            <LineTrend
              data={qualityTrends as unknown as Array<Record<string, number | string>>}
              keys={[
                { key: "submit", label: "提交", color: "#6366f1" },
                { key: "success", label: "成功", color: "#10b981" },
                { key: "failed", label: "失败", color: "#ef4444" },
                { key: "blocked", label: "拦截", color: "#f59e0b" },
              ]}
            />
          </div>
        </section>

        <section className="rounded-xl border border-surface-border bg-white">
          <div className="border-b border-surface-border px-5 py-4">
            <h3 className="text-base font-medium text-text-primary">错误原因 Top 10</h3>
            <p className="mt-1 text-xs text-text-muted">固定展示最近 7 天</p>
          </div>
          <div className="p-5">
            <HorizontalBars
              data={errorTop}
              label={(item) => (item as ErrorRankItem).reasonCode}
              value={(item) => (item as ErrorRankItem).count}
              color="#ef4444"
            />
          </div>
        </section>
      </div>

      <section className="rounded-xl border border-surface-border bg-white">
        <div className="border-b border-surface-border px-5 py-4">
          <h3 className="text-base font-medium text-text-primary">模型 Token 与生图成本</h3>
          <p className="mt-1 text-xs text-text-muted">
            {periodLabel(periodPreset)}按业务入口统计。Token 为模型用量；生图费用预估不包含辅助 LLM Token 费用。
          </p>
        </div>
        <div className="grid grid-cols-2 gap-px bg-surface-border md:grid-cols-5">
          {[
            ["模型 Token", formatNum(entryUsageCosts.summary.totalTokens)],
            ["输入 / 输出 Token", `${formatNum(entryUsageCosts.summary.inputTokens)} / ${formatNum(entryUsageCosts.summary.outputTokens)}`],
            ["生图费用预估", formatMoney(entryUsageCosts.summary.estimatedImageCostUsd, "USD")],
            ["三张图片预估", entryUsageCosts.summary.threeImageEstimateUsd === null ? "暂无预估" : formatMoney(entryUsageCosts.summary.threeImageEstimateUsd, "USD")],
            ["Token 采集覆盖率", formatRate(entryUsageCosts.summary.tokenCoverageRate * 100)],
          ].map(([label, value]) => (
            <div key={label} className="bg-white px-5 py-4">
              <p className="text-xs text-text-muted">{label}</p>
              <p className="mt-1 font-mono text-xl font-semibold text-text-primary">{value}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 gap-6 p-5 xl:grid-cols-2">
          <div>
            <p className="mb-3 text-xs font-medium text-text-secondary">每日 Token 使用量（按入口）</p>
            <DailyEntryStackedBars
              data={entryUsageCosts.dailyEntries}
              metric="totalTokens"
              valueText={formatNum}
            />
          </div>
          <div>
            <p className="mb-3 text-xs font-medium text-text-secondary">每日生图费用预估 USD（按入口）</p>
            <DailyEntryStackedBars
              data={entryUsageCosts.dailyEntries}
              metric="estimatedImageCostUsd"
              valueText={(value) => formatMoney(value, "USD")}
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1080px] text-xs">
            <thead className="bg-surface/50 text-text-secondary">
              <tr>
                <th className="px-3 py-2 text-left font-medium">生图入口</th>
                <th className="px-3 py-2 text-right font-medium">图片数</th>
                <th className="px-3 py-2 text-right font-medium">模型调用</th>
                <th className="px-3 py-2 text-right font-medium">输入 Token</th>
                <th className="px-3 py-2 text-right font-medium">输出 Token</th>
                <th className="px-3 py-2 text-right font-medium">总 Token</th>
                <th className="px-3 py-2 text-right font-medium">生图费用预估</th>
                <th className="px-3 py-2 text-right font-medium">单张平均成本</th>
                <th className="px-3 py-2 text-right font-medium">三张费用预估</th>
                <th className="px-3 py-2 text-right font-medium">Token 覆盖率</th>
              </tr>
            </thead>
            <tbody>
              {entryUsageCosts.entrySummary.map((item) => (
                <tr key={`${item.sourceEntry}-${item.entryId}`} className="border-b border-surface-border even:bg-surface/50">
                  <td className="px-3 py-2 text-text-primary">{item.entryName}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatNum(item.deliveredImages)}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatNum(item.modelCalls)}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatNum(item.inputTokens)}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatNum(item.outputTokens)}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatNum(item.totalTokens)}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatMoney(item.estimatedImageCostUsd, "USD")}</td>
                  <td className="px-3 py-2 text-right font-mono">{item.avgImageCostUsd == null ? "--" : formatMoney(item.avgImageCostUsd, "USD")}</td>
                  <td className="px-3 py-2 text-right font-mono">{item.threeImageEstimateUsd == null ? "暂无预估" : formatMoney(item.threeImageEstimateUsd, "USD")}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatRate(item.tokenCoverageRate * 100)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-xl border border-surface-border bg-white">
        <div className="flex flex-col justify-between gap-3 border-b border-surface-border px-5 py-4 sm:flex-row sm:items-center">
          <div>
            <h3 className="text-base font-medium text-text-primary">最近异常明细</h3>
            <p className="mt-1 text-xs text-text-muted">固定展示最近 7 天，点击 reason_code 可筛选</p>
          </div>
          {selectedReasonCode && (
            <button
              onClick={async () => {
                setSelectedReasonCode("");
                await loadRecentErrors(1, recentErrorsPageSize, "");
              }}
              className="h-8 rounded-lg bg-brand/10 px-3 text-xs font-medium text-brand"
            >
              清除筛选：{selectedReasonCode}
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] text-xs">
            <thead className="bg-surface/50 text-text-secondary">
              <tr>
                <th className="px-3 py-2 text-left font-medium">时间</th>
                <th className="px-3 py-2 text-left font-medium">用户</th>
                <th className="px-3 py-2 text-left font-medium">模型</th>
                <th className="px-3 py-2 text-left font-medium">模式</th>
                <th className="px-3 py-2 text-left font-medium">画质</th>
                <th className="px-3 py-2 text-left font-medium">reason_code</th>
                <th className="px-3 py-2 text-left font-medium">失败原因</th>
                <th className="px-3 py-2 text-right font-medium">积分</th>
                <th className="px-3 py-2 text-right font-medium">操作</th>
              </tr>
            </thead>
            <tbody>
              {recentErrors.map((item) => (
                <tr key={item.genId} className="border-b border-surface-border">
                  <td className="px-3 py-2 text-text-muted">{formatDateTime(item.createdAt)}</td>
                  <td className="px-3 py-2 font-mono text-text-primary">{item.userId}</td>
                  <td className="px-3 py-2 text-text-primary">{item.modelId || "--"}</td>
                  <td className="px-3 py-2 text-text-muted">{item.mode || "--"}</td>
                  <td className="px-3 py-2 text-text-muted">{item.quality || "--"}</td>
                  <td
                    className="cursor-pointer px-3 py-2 font-mono text-red-500"
                    onClick={async () => {
                      const next = selectedReasonCode === item.reasonCode ? "" : item.reasonCode || "";
                      setSelectedReasonCode(next);
                      await loadRecentErrors(1, recentErrorsPageSize, next);
                    }}
                  >
                    {item.reasonCode || "UNKNOWN"}
                  </td>
                  <td className="px-3 py-2 text-text-primary">{item.failReason || "--"}</td>
                  <td className="px-3 py-2 text-right font-mono">{item.pointsConsumed}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => navigate(`/ai/generations/${item.genId}`)}
                      className="text-brand hover:underline"
                    >
                      查看生成
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination
          page={recentErrorsPage}
          totalPages={totalPages}
          total={recentErrorsTotal}
          pageSize={recentErrorsPageSize}
          onPageChange={(nextPage) => loadRecentErrors(nextPage, recentErrorsPageSize)}
          onPageSizeChange={(nextSize) => loadRecentErrors(1, nextSize)}
        />
      </section>
    </div>
  );
}
